/**
 * SocialOutreachButtons — Phase 3 deep-link launcher + contact tracker.
 *
 * Renders a compact row of platform buttons under a realtor card. Each
 * button:
 *   - Mobile: opens the native app deep link (with timeout fallback to web)
 *   - Desktop: copies the web URL + toast confirmation
 *   - Long-press / dropdown: shows the user's templates for that channel
 *     so they can "Copy message" before opening the app
 *
 * Visual: emerald tint = at least one outreach_log entry on that channel;
 * tiny green checkmark badge = the current user marked "followed" on that
 * platform (follow queue).
 *
 * Popover extras (follow queue support):
 *   - "Mark as followed" / unmark — per-user follow tracking
 *   - "Remove this link" — two-tap confirm that clears a mismatched social
 *     URL from the realtor profile (enrichment sometimes picks the wrong
 *     person's page).
 */
import React, { useMemo, useState, useEffect } from "react";
import {
  Facebook, Instagram, Linkedin, Twitter, Youtube,
  MessageCircle, Copy, ExternalLink, Check,
  Loader2, UserPlus, UserCheck, UserMinus, Unlink, ShieldCheck,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  PLATFORMS, isMobileDevice, buildOpenStrategy, copyToClipboard,
} from "@/lib/outreach";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { TikTokIcon, ThreadsIcon } from "@/components/social/PlatformIcons";
import ConfirmProfileDialog from "@/components/social/ConfirmProfileDialog";

// TikTokIcon + ThreadsIcon now live in components/social/PlatformIcons.jsx
// so PendingSocialsBadge can share the exact same SVGs (and any fixes
// like Threads' fill-rule="evenodd" cutout land in both places at once).

export const ICON_BY_KEY = {
  fb: Facebook,
  ig: Instagram,
  li: Linkedin,
  x: Twitter,
  tt: TikTokIcon,
  yt: Youtube,
  th: ThreadsIcon,
};

export const COLOR_BY_KEY = {
  fb: "text-[#1877F2] hover:bg-blue-50",
  ig: "text-[#E1306C] hover:bg-pink-50",
  li: "text-[#0A66C2] hover:bg-sky-50",
  x: "text-slate-900 hover:bg-slate-100",
  tt: "text-slate-900 hover:bg-slate-100",
  yt: "text-[#FF0000] hover:bg-red-50",
  th: "text-slate-900 hover:bg-slate-100",
};

export default function SocialOutreachButtons({
  realtor,
  onContactLogged,
  compact = false,
  onFollowChanged,
  onLinkRemoved,
  onSocialVerified,
  platformOrder,
}) {
  // Active platforms: only show buttons for channels where the realtor
  // actually has a URL on file (no need to surface dead buttons). When a
  // `platformOrder` (channel keys, most-popular first) is supplied, the
  // buttons are reordered to match — the Outreach queue passes the scoped
  // channel-popularity order so common channels lead.
  const activePlatforms = useMemo(() => {
    const list = PLATFORMS.filter((p) => (realtor?.[p.urlField] || "").trim());
    if (platformOrder && platformOrder.length) {
      const idx = Object.fromEntries(platformOrder.map((k, i) => [k, i]));
      list.sort((a, b) => (idx[a.key] ?? 99) - (idx[b.key] ?? 99));
    }
    return list;
  }, [realtor, platformOrder]);

  // Per-channel contacted-at lookup derived from outreach_log.
  const contactedByChannel = useMemo(() => {
    const out = {};
    for (const e of realtor?.outreach_log || []) {
      const t = (e.type || "").toLowerCase();
      // Opening a profile is audit-only — never a contact.
      if (t === "viewed_profile") continue;
      let ch = e.channel || "";
      // Legacy entries: dm_ig -> ig
      if (!ch && t.startsWith("dm_")) ch = t.slice(3);
      if (!ch) continue;
      const ts = e.timestamp || "";
      if (!out[ch] || ts > out[ch]) out[ch] = ts;
    }
    return out;
  }, [realtor]);

  if (activePlatforms.length === 0) return null;

  return (
    <div
      className={cn("flex flex-wrap items-center gap-1", compact && "gap-0.5")}
      data-testid={`social-buttons-${realtor.id}`}
    >
      {activePlatforms.map((p) => (
        <PlatformButton
          key={p.key}
          platform={p}
          realtor={realtor}
          contactedAt={contactedByChannel[p.key]}
          followedAt={(realtor.follows || {})[p.key]}
          onContactLogged={onContactLogged}
          onFollowChanged={onFollowChanged}
          onLinkRemoved={onLinkRemoved}
          onSocialVerified={onSocialVerified}
          compact={compact}
        />
      ))}
    </div>
  );
}

function PlatformButton({
  platform, realtor, contactedAt, followedAt,
  onContactLogged, onFollowChanged, onLinkRemoved, onSocialVerified, compact,
}) {
  const Icon = ICON_BY_KEY[platform.key];
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [verifyDialog, setVerifyDialog] = useState(null); // {mode: 'quickOpen'|'template', template?, strategy}
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [urlOverride, setUrlOverride] = useState(null); // local override after changeLink
  // Clear override when the parent prop catches up (e.g. data refresh)
  const propUrl = realtor[platform.urlField] || "";
  useEffect(() => {
    if (urlOverride && propUrl === urlOverride) setUrlOverride(null);
  }, [propUrl, urlOverride]);
  const contacted = !!contactedAt;
  const followed = !!followedAt;
  const rawUrl = urlOverride || realtor[platform.urlField] || "";
  const strategy = useMemo(() => buildOpenStrategy(platform.key, rawUrl), [platform.key, rawUrl]);

  // First-click verification: the confirm dialog only fires the first
  // time a user opens a given social link. Approving it stores the URL
  // in verified_socials so future opens go straight through.
  const verifiedEntry = (realtor.verified_socials || {})[platform.urlField];
  const isVerified = !!(verifiedEntry && verifiedEntry.url === rawUrl && rawUrl);

  const loadTemplates = async () => {
    if (templates !== null || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get("/templates", { params: { channel: platform.key } });
      setTemplates(data || []);
    } catch (e) {
      toast.error("Couldn't load templates", { description: formatApiError(e) });
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Quick-open: log viewed_profile + open native app (with web fallback)
  const quickOpen = async () => {
    if (!isVerified) {
      // Alex-Ward UX (Feb 2026): open the profile immediately AND show
      // the confirm dialog so the user can eyeball the page in a new
      // tab while they answer "correct?". We don't log the outreach yet
      // — that happens after they press "Correct".
      openLink(strategy);
      setVerifyDialog({ mode: "quickOpen", peeked: true });
      return;
    }
    await performQuickOpen();
  };

  const performQuickOpen = async ({ skipOpen = false } = {}) => {
    if (!skipOpen) {
      await openLink(strategy);
    }
    try {
      await api.post(`/realtors/${realtor.id}/outreach`, {
        type: "viewed_profile",
        channel: platform.key,
        note: "Opened profile",
      });
      onContactLogged?.(realtor.id);
    } catch {
      /* non-blocking */
    }
  };

  // Template flow: copy rendered template to clipboard, open native app,
  // log dm_<channel> outreach entry.
  const applyTemplate = async (template) => {
    if (!isVerified) {
      // Peek the profile in a new tab so the user can compare it against
      // the message they're about to send before confirming.
      openLink(strategy);
      setVerifyDialog({ mode: "template", template, peeked: true });
      return;
    }
    await performApplyTemplate(template);
  };

  const performApplyTemplate = async (template, { skipOpen = false } = {}) => {
    setRendering(true);
    try {
      const { data } = await api.post(`/templates/${template.id}/render`, {
        realtor_id: realtor.id,
      });
      const ok = await copyToClipboard(data.body_rendered);
      if (ok) {
        toast.success(`Message copied · opening ${platform.label}…`, {
          description: data.body_rendered.slice(0, 140) + (data.body_rendered.length > 140 ? "…" : ""),
        });
      } else {
        toast.warning("Message ready but clipboard failed — copy manually below", {
          description: data.body_rendered,
          duration: 10000,
        });
      }
      if (!skipOpen) {
        await openLink(strategy);
      }
      await api.post(`/realtors/${realtor.id}/outreach`, {
        type: `dm_${platform.key}`,
        channel: platform.key,
        template_id: template.id,
        note: `Used template: ${template.name}`,
      });
      setOpen(false);
      onContactLogged?.(realtor.id);
    } catch (e) {
      toast.error("Couldn't render template", { description: formatApiError(e) });
    } finally {
      setRendering(false);
    }
  };

  const confirmProfileCorrect = async ({ alsoFollow = false } = {}) => {
    setVerifyBusy(true);
    try {
      const { data } = await api.post(`/realtors/${realtor.id}/socials/verify`, {
        field: platform.urlField,
      });
      // Update the row in place — no full list reload → scroll position
      // stays put while the user works through a single realtor's socials.
      // The verify endpoint returns the full realtor doc; pluck the fresh
      // verified_socials entry (falls back to a synthesized one if the
      // API shape ever changes).
      const entry = (data?.verified_socials || {})[platform.urlField] || {
        url: rawUrl,
        verified_at: new Date().toISOString(),
      };
      onSocialVerified?.(realtor.id, platform.urlField, entry);
      if (alsoFollow && !followed) {
        // Same tap also records the follow — saves re-opening the popover
        // just to hit "Mark as followed" after confirming a correct profile.
        const { data: f } = await api.post(`/realtors/${realtor.id}/follows/${platform.key}`);
        onFollowChanged?.(realtor.id, platform.key, f.followed_at);
        toast.success(`${platform.label} verified + followed`, {
          description: `${realtor.first_name || realtor.name || "Realtor"} — green check saved`,
        });
      } else {
        toast.success(`${platform.label} profile marked verified`);
      }
      const mode = verifyDialog?.mode;
      const tpl = verifyDialog?.template;
      const alreadyOpened = !!verifyDialog?.peeked;
      setVerifyDialog(null);
      if (mode === "template" && tpl) {
        // If we already peeked the profile in a new tab, don't spawn a
        // second one — just render + copy the template and log the touch.
        await performApplyTemplate(tpl, { skipOpen: alreadyOpened });
      } else {
        await performQuickOpen({ skipOpen: alreadyOpened });
      }
    } catch (e) {
      toast.error(alsoFollow ? "Couldn't verify + follow" : "Couldn't verify link", { description: formatApiError(e) });
    } finally {
      setVerifyBusy(false);
    }
  };

  const confirmProfileWrong = async () => {
    setVerifyBusy(true);
    try {
      await api.post(`/realtors/${realtor.id}/socials/mark-wrong`, {
        field: platform.urlField,
        reason: "wrong_profile_on_first_open",
      });
      toast.success(`${platform.label} link removed`, {
        description: "Marked for replacement — re-enrich to search for the right profile.",
      });
      setVerifyDialog(null);
      setOpen(false);
      onLinkRemoved?.(realtor.id, platform.urlField);
    } catch (e) {
      toast.error("Couldn't flag link", { description: formatApiError(e) });
    } finally {
      setVerifyBusy(false);
    }
  };

  const changeLink = async (newUrl, { alsoFollow = false } = {}) => {
    setVerifyBusy(true);
    try {
      const { data } = await api.post(`/realtors/${realtor.id}/socials/change-link`, {
        field: platform.urlField,
        new_url: newUrl,
      });
      const entry = (data?.verified_socials || {})[platform.urlField] || {
        url: newUrl,
        verified_at: new Date().toISOString(),
      };
      onSocialVerified?.(realtor.id, platform.urlField, entry);
      // Update local URL immediately so next click opens the new link
      setUrlOverride(newUrl);

      if (alsoFollow && !followed) {
        try {
          // Verify is already done by change-link, just follow
          const { data: fData } = await api.post(`/realtors/${realtor.id}/follows/${platform.key}`);
          onFollowChanged?.(realtor.id, platform.key, fData.followed_at);
        } catch { /* follow is best-effort */ }
      }

      toast.success(`${platform.label} link updated & verified${alsoFollow ? " & followed" : ""}`, {
        description: `Old link logged as incorrect — new link saved.`,
      });
      setVerifyDialog(null);
      setOpen(false);
    } catch (e) {
      toast.error("Couldn't change link", { description: formatApiError(e) });
    } finally {
      setVerifyBusy(false);
    }
  };

  const markContacted = async () => {
    try {
      await api.post(`/realtors/${realtor.id}/outreach`, {
        type: `dm_${platform.key}`,
        channel: platform.key,
        note: "Manually marked contacted",
      });
      toast.success(`Marked contacted on ${platform.label}`);
      setOpen(false);
      onContactLogged?.(realtor.id);
    } catch (e) {
      toast.error("Couldn't mark contacted", { description: formatApiError(e) });
    }
  };

  // Follow-queue: per-user "I followed them on this platform" toggle.
  // On follow (not unfollow), also verify the link so Cleanup Section 8
  // won't flag it as suspect — same as the first-open confirm dialog.
  const toggleFollowed = async () => {
    try {
      if (followed) {
        await api.delete(`/realtors/${realtor.id}/follows/${platform.key}`);
        onFollowChanged?.(realtor.id, platform.key, null);
        toast.success(`Marked as unfollowed on ${platform.label}`);
      } else {
        // Verify first (if not already) so the link is protected from cleanup
        if (!isVerified) {
          try {
            const { data: vData } = await api.post(`/realtors/${realtor.id}/socials/verify`, {
              field: platform.urlField,
            });
            const entry = (vData?.verified_socials || {})[platform.urlField] || {
              url: rawUrl,
              verified_at: new Date().toISOString(),
            };
            onSocialVerified?.(realtor.id, platform.urlField, entry);
          } catch {
            /* verify is best-effort — don't block the follow */
          }
        }
        const { data } = await api.post(`/realtors/${realtor.id}/follows/${platform.key}`);
        onFollowChanged?.(realtor.id, platform.key, data.followed_at);
        toast.success(`Followed on ${platform.label}`, {
          description: `${realtor.first_name || realtor.name || "Realtor"} — verified + green check saved`,
        });
      }
      setOpen(false);
    } catch (e) {
      toast.error("Couldn't update follow state", { description: formatApiError(e) });
    }
  };

  // Link hygiene: two-tap confirm to clear a wrong-person social URL.
  const removeLink = async () => {
    if (!confirmRemove) {
      setConfirmRemove(true);
      return;
    }
    try {
      await api.patch(`/realtors/${realtor.id}`, { [platform.urlField]: "" });
      toast.success(`${platform.label} link removed`, {
        description: "The URL was cleared from this profile.",
      });
      setOpen(false);
      onLinkRemoved?.(realtor.id, platform.urlField);
    } catch (e) {
      toast.error("Couldn't remove link", { description: formatApiError(e) });
    } finally {
      setConfirmRemove(false);
    }
  };

  const sizeClass = compact ? "h-7 w-7" : "h-8 w-8";

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        setConfirmRemove(false);
        if (o) loadTemplates();
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`${platform.label}${isVerified ? ", verified" : ""}${followed ? ", followed" : ""}`}
          className={cn(
            "rounded-md p-1.5 border border-transparent transition-colors flex items-center justify-center relative",
            sizeClass,
            COLOR_BY_KEY[platform.key],
            followed && "bg-emerald-100 border-emerald-500",
            !followed && isVerified && "border-emerald-400",
          )}
          data-testid={`social-btn-${platform.key}-${realtor.id}`}
          title={`${platform.label}${isVerified ? " · verified" : ""}${followed ? ` · followed ${formatRelative(followedAt)}` : ""}`}
        >
          <Icon className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
          {followed ? (
            <span
              className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-500 ring-1 ring-white flex items-center justify-center"
              data-testid={`social-followed-check-${platform.key}-${realtor.id}`}
            >
              <Check className="h-2 w-2 text-white" strokeWidth={4} />
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 max-w-[calc(100vw-2rem)] p-2"
        data-testid={`social-popover-${platform.key}-${realtor.id}`}
      >
        <div className="flex items-center justify-between mb-2 px-1">
          <div className="text-sm font-semibold text-slate-900">{platform.label}</div>
          <div className="flex items-center gap-1">
            {isVerified ? (
              <Badge
                variant="outline"
                className="text-[10px] gap-1 bg-emerald-50 border-emerald-200 text-emerald-700"
                data-testid={`social-verified-badge-${platform.key}-${realtor.id}`}
              >
                <ShieldCheck className="h-3 w-3" /> Verified
              </Badge>
            ) : null}
            {followed ? (
              <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-600 border-emerald-600 text-white">
                <UserCheck className="h-3 w-3" /> Followed
              </Badge>
            ) : null}
          </div>
        </div>

        {/* Quick open — no template */}
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start mb-1 h-8"
          onClick={quickOpen}
          data-testid={`social-quick-open-${platform.key}-${realtor.id}`}
        >
          <ExternalLink className="h-3.5 w-3.5 mr-2" />
          Open profile
        </Button>

        {/* Follow tracking — the queue's primary action */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "w-full justify-start text-xs h-8 mb-1",
            followed ? "text-rose-600 hover:text-rose-700 hover:bg-rose-50" : "font-medium",
          )}
          onClick={toggleFollowed}
          data-testid={`social-follow-${platform.key}-${realtor.id}`}
        >
          {followed ? (
            <UserMinus className="h-3.5 w-3.5 mr-2" />
          ) : (
            <UserPlus className="h-3.5 w-3.5 mr-2" />
          )}
          {followed ? "Mark as Unfollowed" : "Mark as followed"}
        </Button>

        {/* Explicit verify — lets the user confirm a correct profile
            without going through the first-open confirmation flow. */}
        {!isVerified && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-xs h-8 mb-1 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 font-medium"
            onClick={async () => {
              try {
                const { data } = await api.post(`/realtors/${realtor.id}/socials/verify`, {
                  field: platform.urlField,
                });
                const entry = (data?.verified_socials || {})[platform.urlField] || {
                  url: rawUrl,
                  verified_at: new Date().toISOString(),
                };
                onSocialVerified?.(realtor.id, platform.urlField, entry);
                toast.success(`${platform.label} profile marked verified`);
                setOpen(false);
              } catch (e) {
                toast.error("Couldn't verify link", { description: formatApiError(e) });
              }
            }}
            data-testid={`social-verify-btn-${platform.key}-${realtor.id}`}
          >
            <ShieldCheck className="h-3.5 w-3.5 mr-2" />
            Mark as Verified
          </Button>
        )}

        {/* Template picker */}
        <div className="border-t border-slate-100 my-1.5" />
        <div className="px-1 text-[10px] uppercase tracking-wider text-slate-500 font-medium mb-1">
          Copy message + open
        </div>
        {loading || templates === null ? (
          <div className="flex items-center justify-center py-3 text-xs text-slate-500">
            <Loader2 className="h-3 w-3 mr-2 animate-spin" /> Loading templates…
          </div>
        ) : templates.length === 0 ? (
          <div className="px-1 py-2 text-xs text-slate-500">
            No templates for {platform.label} yet.{" "}
            <a href="/templates" className="text-slate-900 underline font-medium">Create one</a>
          </div>
        ) : (
          <div className="max-h-44 overflow-y-auto space-y-0.5">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                disabled={rendering}
                onClick={() => applyTemplate(t)}
                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-slate-50 text-xs flex items-center gap-2 disabled:opacity-50"
                data-testid={`social-template-${t.id}-${realtor.id}`}
              >
                <Copy className="h-3 w-3 text-slate-500 shrink-0" />
                <span className="flex-1 truncate text-slate-900 font-medium">{t.name}</span>
                {t.promo_code ? (
                  <span className="text-[10px] text-slate-500 tabular-nums shrink-0">{t.promo_code}</span>
                ) : null}
              </button>
            ))}
          </div>
        )}

        {/* Manual mark */}
        <div className="border-t border-slate-100 my-1.5" />
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-xs h-7"
          onClick={markContacted}
          data-testid={`social-mark-${platform.key}-${realtor.id}`}
        >
          <MessageCircle className="h-3 w-3 mr-2" />
          {contacted ? "Log another touch" : "Mark contacted"}
        </Button>

        {/* Link hygiene — clear a mismatched profile URL (two-tap confirm) */}
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "w-full justify-start text-xs h-7",
            confirmRemove
              ? "text-white bg-rose-600 hover:bg-rose-700 hover:text-white"
              : "text-rose-600 hover:text-rose-700 hover:bg-rose-50",
          )}
          onClick={removeLink}
          data-testid={`social-remove-link-${platform.key}-${realtor.id}`}
        >
          <Unlink className="h-3 w-3 mr-2" />
          {confirmRemove ? "Tap again to confirm removal" : "Remove this link (wrong profile)"}
        </Button>
      </PopoverContent>

      {/* First-open confirmation — fires the first time this social is
          opened. Approving stores the URL in verified_socials so future
          taps skip the prompt. Rejecting clears the field + logs it.
          Dialog UI is shared with PendingSocialsBadge so both flows look
          identical to the operator. */}
      <ConfirmProfileDialog
        open={!!verifyDialog}
        onOpenChange={(o) => { if (!o) setVerifyDialog(null); }}
        busy={verifyBusy}
        platformLabel={platform.label}
        realtor={realtor}
        url={rawUrl}
        wrongLabel="Wrong profile"
        correctLabel="Correct — mark verified"
        correctAndFollowLabel={!followed ? "Correct - Mark as followed" : undefined}
        onWrong={confirmProfileWrong}
        onCorrect={() => confirmProfileCorrect()}
        onCorrectAndFollow={!followed ? () => confirmProfileCorrect({ alsoFollow: true }) : undefined}
        onChangeLink={changeLink}
        testidPrefix={`social-verify-${platform.key}-${realtor.id}`}
      />
    </Popover>
  );
}

/**
 * Try the native deep link first; if nothing happens within ~700ms (because
 * the URI scheme had no handler) navigate to the web URL. On desktop we just
 * copy the web URL + open it in a new tab.
 *
 * NOTE: browsers swallow the URI handler when no app is installed without
 * raising any event we can hook into — the visibility-change + timeout
 * pattern is the de facto workaround. Works reliably on iOS / Android.
 */
async function openLink({ native, web }) {
  if (!native && !web) return;
  if (!isMobileDevice()) {
    // Desktop: open web URL in new tab, also copy to clipboard for paste use
    if (web) {
      await copyToClipboard(web);
      window.open(web, "_blank", "noopener,noreferrer");
    }
    return;
  }
  if (!native) {
    window.location.href = web;
    return;
  }
  // Mobile attempt: set up visibility-change watcher to detect successful app launch
  let didSwitch = false;
  const onVis = () => {
    if (document.hidden || document.visibilityState === "hidden") {
      didSwitch = true;
    }
  };
  document.addEventListener("visibilitychange", onVis);
  window.location.href = native;
  setTimeout(() => {
    document.removeEventListener("visibilitychange", onVis);
    if (!didSwitch && web) {
      window.location.href = web;
    }
  }, 800);
}

function formatRelative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso);
    const diff = (Date.now() - then.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.round(diff / 86400)}d ago`;
    return then.toLocaleDateString();
  } catch {
    return "";
  }
}
